import { useEffect, useState } from 'react';
import { api } from '../../api/client';
import { t } from '../../hooks/useT';

interface AdminStats {
  totalUsers: number;
  newUsers7d: number;
  newUsers30d: number;
  premiumUsers: number;
  runsToday: number;
  runs7d: number;
  winRatePct: number | null;
  arenaMatches30d: number;
  stripePurchases30d: number;
  d1RetentionPct: number | null;
  signupGrowth: { date: string; new_users: number }[];
  puzzlesByStatus: { pool: string; status: string; count: number }[];
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="admin-stat-card">
      <span className="admin-stat-value">{value}</span>
      <span className="admin-stat-label">{label}</span>
    </div>
  );
}

function fmtPct(v: number | null) {
  return v == null ? t('admin.stats.noData') : `${v}%`;
}

export function AdminStatsTab() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api<AdminStats>('/admin/stats')
      .then(setStats)
      .catch(() => setError(t('common.error')));
  }, []);

  if (error) return <p className="admin-msg">{error}</p>;
  if (!stats) return <p className="admin-msg">{t('common.loading')}</p>;

  return (
    <div className="admin-stats">
      <div className="admin-stat-grid">
        <StatCard label={t('admin.stats.totalUsers')} value={stats.totalUsers} />
        <StatCard label={t('admin.stats.newUsers7d')} value={stats.newUsers7d} />
        <StatCard label={t('admin.stats.newUsers30d')} value={stats.newUsers30d} />
        <StatCard label={t('admin.stats.premiumUsers')} value={stats.premiumUsers} />
        <StatCard label={t('admin.stats.runsToday')} value={stats.runsToday} />
        <StatCard label={t('admin.stats.runs7d')} value={stats.runs7d} />
        <StatCard label={t('admin.stats.winRate')} value={fmtPct(stats.winRatePct)} />
        <StatCard label={t('admin.stats.arenaMatches')} value={stats.arenaMatches30d} />
        <StatCard label={t('admin.stats.stripePurchases')} value={stats.stripePurchases30d} />
        <StatCard label={t('admin.stats.d1Retention')} value={fmtPct(stats.d1RetentionPct)} />
      </div>

      {stats.puzzlesByStatus.length > 0 && (
        <section className="admin-panel-section">
          <h2>{t('admin.stats.puzzlesBreakdown')}</h2>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Pool</th>
                <th>Status</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {stats.puzzlesByStatus.map((row) => (
                <tr key={`${row.pool}-${row.status}`}>
                  <td>{row.pool}</td>
                  <td>{row.status}</td>
                  <td>{row.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {stats.signupGrowth.length > 0 && (
        <section className="admin-panel-section">
          <h2>{t('admin.stats.signupGrowth')}</h2>
          <table className="admin-table admin-table-compact">
            <tbody>
              {stats.signupGrowth.map((row) => (
                <tr key={row.date}>
                  <td>{row.date}</td>
                  <td>{row.new_users}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
